/**
 * @file PrivateRoutes.tsx
 * @module app/routing/PrivateRoutes
 * @description Defines role-specific PrivateRoutes route mappings, protecting UTrade Admin screens and selecting the appropriate application views.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { lazy, FC, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { MasterLayout } from "../../_admin/layout/MasterLayout";
import TopBarProgress from "react-topbar-progress-indicator";
// import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper';
import { getCSSVariableValue } from "../../_admin/assets/ts/_utils";
import { WithChildren } from "../../_admin/helpers";
import AdvertPage from "../modules/advertisement/index";
import PlatformFees from "../modules/platform-fees/index";
import ContactPage from "../modules/contact-inquiries/index";
import AllProducts from "../modules/all-products/index";
// import Customers from "../modules/customers/index";
import Customers from "../modules/customers/index";
import Orders from "../modules/orders-delivery/index";
// import InventoryPage from "../modules/Inventory";
import CustomerService from "../modules/customer-service/index";
import PaymentsIndex from "../modules/finance";
import OrdersDelivery from "../modules/orders-delivery/pages/finance/orders-delivery-finance";
import DashboardIndex from "../modules/dashboard";
import UProcureDashboardIndex from "../modules/uProcure/dashboard";
import CustomNotification from "../modules/custom-notification/index";
import FinanceReport from "../modules/finance-reports/index";
import InventoryReplenishmentReport from "../modules/uProcure/inventory-replenishment/inventory-replenishment-report";
import SettingPage from "../modules/setting";
import Reports from "../modules/reports";
import Method from "../../utils/methods";
import { useAuth } from "../modules/auth";
import InterTransferRequestPage from "../modules/inter-transfer-request";
import PriceUpdateRequestPage from "../modules/price-update-request";
import CustomerCategoryPage from "../modules/customer-category";
import PendingDeliveryOrders from "../modules/pending-delivery-orders/index";
import OutstandingReport from "../modules/outstanding-report/index";
import ActivityLogs from "../modules/activity-logs";
import WarehousePage from "../modules/warehouse";
import VendorsManagementPage from "../modules/uProcure/vendors_management";
import PurchaseOrderManagementPage from "../modules/uProcure/po-management";
import AssignProductsToVendorsPage from "../modules/uProcure/assign-product-to-vendors";
import UProcureMasterPage from "../modules/uProcure/master";
import UProcureProductsPage from "../modules/uProcure/products";
import EscalationPage from "../modules/uProcure/escalations";
import GoodsReceivedNotePage from "../modules/uProcure/goods-received-note";
import InwardPage from "../modules/inward";
import NotificationsList from "../modules/notifications";
import VendorLedgerLayoutPage from "../modules/uProcure/vendor-ledger";
import SalesManagementIndex from "../modules/sales-management";
import PriceCatalogPage from "../modules/price-catalog/index";
import { PageTitle } from "../../_admin/layout/core";

const PrivateRoutes = () => {
  const { currentUser } = useAuth();

  const SellerPage = lazy(() => import("../modules/seller/index"));
  const MasterPage = lazy(() => import("../modules/master/index"));
  const MasterNewPage = lazy(() => import("../modules/master-new/index"));
  const ProcurementPage = lazy(() => import("../modules/procurement/index"));
  const ProductPage = lazy(() => import("../modules/products/index"));
  const InventoryPage = lazy(() => import("../modules/Inventory/index"));
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path="*" element={<Navigate to="/error/404" />} />
        <Route
          path="auth/*"
          element={
            <Navigate to={Method.getFirstAuthorizedRoute(currentUser)} />
          }
        />
        {/* Pages */}
        <Route path="dashboard/*" element={<DashboardIndex />} />
        <Route path="sales-management/*" element={<SalesManagementIndex />} />
        <Route
          path="UProcure/master/*"
          element={
            <SuspensedView>
              <UProcureMasterPage />
            </SuspensedView>
          }
        />
        <Route
          path="UProcure/products/*"
          element={
            <SuspensedView>
              <UProcureProductsPage />
            </SuspensedView>
          }
        />
        <Route
          path="/UProcure/vendors/*"
          element={
            <SuspensedView>
              <VendorsManagementPage />
            </SuspensedView>
          }
        />
        <Route
          path="/UProcure/master/dashboard/*"
          element={
            <SuspensedView>
              <UProcureDashboardIndex />
            </SuspensedView>
          }
        />
        <Route path="/UProcure/ats-report" element={<Navigate to="/UProcure/inventory-replenishment-report" replace />} />
        <Route
          path="/UProcure/inventory-replenishment-report"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={[{ title: "UProcure", path: "/UProcure/purchase-order-management", isSeparator: false, isActive: false }, { title: "", path: "", isSeparator: true, isActive: false }]}>
                Inventory Replenishment Report
              </PageTitle>
              <InventoryReplenishmentReport />
            </SuspensedView>
          }
        />
        <Route
          path="/UProcure/purchase-order-management/*"
          element={
            <SuspensedView>
              <PurchaseOrderManagementPage />
            </SuspensedView>
          }
        />
        <Route
          path="/UProcure/escalations/*"
          element={
            <SuspensedView>
              <EscalationPage />
            </SuspensedView>
          }
        />
        <Route
          path="/UProcure/vendor-ledger/*"
          element={
            <SuspensedView>
              <VendorLedgerLayoutPage />
            </SuspensedView>
          }
        />
        <Route
          path="/UProcure/goods-received-note/*"
          element={
            <>
              <SuspensedView>
                <GoodsReceivedNotePage />
              </SuspensedView>
            </>
          }
        />
        <Route
          path="/UProcure/inward/*"
          element={
            <SuspensedView>
              <InwardPage />
            </SuspensedView>
          }
        />
        <Route
          path="/UProcure/assign-product-to-vendors/*"
          element={
            <SuspensedView>
              <AssignProductsToVendorsPage />
            </SuspensedView>
          }
        />
        <Route
          path="orders-delivery/*"
          element={
            <SuspensedView>
              <OrdersDelivery />
            </SuspensedView>
          }
        />
        <Route
          path="sellers/*"
          element={
            <SuspensedView>
              <SellerPage />
            </SuspensedView>
          }
        />
        <Route
          path="reports/*"
          element={
            <SuspensedView>
              <Reports />
            </SuspensedView>
          }
        />
        {/* <Route
          path="products/*"
          element={
            <SuspensedView>
              <ProductsIndex />
            </SuspensedView>
          }
        /> */}
        <Route
          path="all-products/*"
          element={
            <SuspensedView>
              <AllProducts />
            </SuspensedView>
          }
        />
        <Route
          path="master-old/*"
          element={
            <SuspensedView>
              <MasterPage />
            </SuspensedView>
          }
        />
        <Route
          path="master/*"
          element={
            <SuspensedView>
              <MasterNewPage />
            </SuspensedView>
          }
        />
        <Route
          path="procurement/*"
          element={
            <SuspensedView>
              <ProcurementPage />
            </SuspensedView>
          }
        />
        <Route
          path="finance-reports/*"
          element={
            <SuspensedView>
              <FinanceReport />
            </SuspensedView>
          }
        />
        {/* <Route
          path="customer-service/*"
          element={
            <SuspensedView>
              <CustomerService />
            </SuspensedView>
          }
        /> */}
        <Route
          path="products/*"
          element={
            <SuspensedView>
              <ProductPage />
            </SuspensedView>
          }
        />
        <Route
          path="advertisement/*"
          element={
            <SuspensedView>
              <AdvertPage />
            </SuspensedView>
          }
        />
        <Route
          path="platform-fees/*"
          element={
            <SuspensedView>
              <PlatformFees />
            </SuspensedView>
          }
        />
        <Route
          path="contact-inquiries/*"
          element={
            <SuspensedView>
              <ContactPage />
            </SuspensedView>
          }
        />
        <Route
          path="customers/*"
          element={
            <SuspensedView>
              <Customers />
            </SuspensedView>
          }
        />
        <Route
          path="payments/*"
          element={
            <SuspensedView>
              <PaymentsIndex />
            </SuspensedView>
          }
        />
        <Route
          path="all-products/*"
          element={
            <SuspensedView>
              <AllProducts />
            </SuspensedView>
          }
        />
        <Route
          path="orders/*"
          element={
            <SuspensedView>
              <Orders />
            </SuspensedView>
          }
        />
        <Route
          path="pending-delivery-orders/*"
          element={
            <SuspensedView>
              <PendingDeliveryOrders />
            </SuspensedView>
          }
        />
        <Route
          path="outstanding-report/*"
          element={
            <SuspensedView>
              <OutstandingReport />
            </SuspensedView>
          }
        />
        <Route
          path="master-old/*"
          element={
            <SuspensedView>
              <MasterPage />
            </SuspensedView>
          }
        />
        {/* <Route
          path="customer-service/*"
          element={
            <SuspensedView>
              <CustomerService />
            </SuspensedView>
          }
        /> */}
        <Route
          path="inventory/*"
          element={
            <SuspensedView>
              <InventoryPage />
            </SuspensedView>
          }
        />
        <Route
          path="warehouse/*"
          element={
            <SuspensedView>
              <WarehousePage />
            </SuspensedView>
          }
        />
        <Route
          path="customer-category/*"
          element={
            <SuspensedView>
              <CustomerCategoryPage />
            </SuspensedView>
          }
        />
        <Route
          path="inter-transfer-request/*"
          element={
            <SuspensedView>
              <InterTransferRequestPage />
            </SuspensedView>
          }
        />
        <Route
          path="price-update-request/*"
          element={
            <SuspensedView>
              <PriceUpdateRequestPage />
            </SuspensedView>
          }
        />
        <Route
          path="products/*"
          element={
            <SuspensedView>
              <ProductPage />
            </SuspensedView>
          }
        />
        <Route
          path="advertisement/*"
          element={
            <SuspensedView>
              <AdvertPage />
            </SuspensedView>
          }
        />
        <Route
          path="custom-notification/*"
          element={
            <SuspensedView>
              <CustomNotification />
            </SuspensedView>
          }
        />
        <Route
          path="platform-fees/*"
          element={
            <SuspensedView>
              <PlatformFees />
            </SuspensedView>
          }
        />
        <Route
          path="contact-inquiries/*"
          element={
            <SuspensedView>
              <ContactPage />
            </SuspensedView>
          }
        />
        <Route
          path="setting/*"
          element={
            <SuspensedView>
              <SettingPage />
            </SuspensedView>
          }
        />
        <Route
          path="logs/*"
          element={
            <SuspensedView>
              <PageTitle>Audit Logs</PageTitle>
              <ActivityLogs />
            </SuspensedView>
          }
        />
        <Route
          path="price-catalog/*"
          element={
            <SuspensedView>
              <PriceCatalogPage />
            </SuspensedView>
          }
        />
        <Route
          path="notifications"
          element={
            <SuspensedView>
              <PageTitle>Notifications</PageTitle>
              <NotificationsList />
            </SuspensedView>
          }
        />
      </Route>
    </Routes>
  );
};
const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: { "0": baseColor },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};
export { PrivateRoutes };
